export type FileKind =
  | 'pdf'
  | 'jpeg'
  | 'png'
  | 'webp'
  | 'heic'
  | 'html'
  | 'zip'
  | 'docx'
  | 'pptx'
  | 'xlsx'
  | 'unknown';

export const FILE_VALIDATION_CODES = {
  emptyFile: 'file.empty',
  unsupportedMagic: 'file.magic.unsupported',
  expectedKindMismatch: 'file.magic.expectedKindMismatch',
  declaredMimeMismatch: 'file.declaredMimeMismatch',
  extensionMismatch: 'file.extensionMismatch',
  archiveTooLarge: 'archive.tooLarge',
  archiveEocdMissing: 'archive.eocdMissing',
  archiveEocdInvalid: 'archive.eocdInvalid',
  archiveMultiDiskUnsupported: 'archive.multiDiskUnsupported',
  archiveZip64Unsupported: 'archive.zip64Unsupported',
  archiveTooManyEntries: 'archive.tooManyEntries',
  archiveCentralDirectoryInvalid: 'archive.centralDirectoryInvalid',
  archiveCentralDirectorySizeMismatch: 'archive.centralDirectorySizeMismatch',
  archiveEntryHeaderInvalid: 'archive.entryHeaderInvalid',
  archiveEntryNameEncodingUnsupported: 'archive.entryNameEncodingUnsupported',
  archiveUnsafeEntryPath: 'archive.unsafeEntryPath',
  archiveDuplicateEntry: 'archive.duplicateEntry',
  archiveEncryptedEntry: 'archive.encryptedEntry',
  archiveCompressionMethodUnsupported: 'archive.compressionMethodUnsupported',
  archiveEntryTooLarge: 'archive.entryTooLarge',
  archiveExpandedSizeTooLarge: 'archive.expandedSizeTooLarge',
  archiveCompressionRatioTooHigh: 'archive.compressionRatioTooHigh',
  archiveLocalHeaderInvalid: 'archive.localHeaderInvalid',
  ooxmlAmbiguousPackage: 'ooxml.ambiguousPackage',
  ooxmlMissingContentTypes: 'ooxml.missingContentTypes',
  ooxmlMissingRootRelationships: 'ooxml.missingRootRelationships',
  ooxmlMissingMainPart: 'ooxml.missingMainPart',
} as const;

export type FileValidationCode =
  (typeof FILE_VALIDATION_CODES)[keyof typeof FILE_VALIDATION_CODES];

export type ValidationDetailValue = string | number | boolean;

export interface FileValidationDiagnostic {
  readonly code: FileValidationCode;
  readonly severity: 'error' | 'warning';
  readonly details?: Readonly<Record<string, ValidationDetailValue>>;
}

export interface ArchiveValidationPolicy {
  readonly maxArchiveBytes: number;
  readonly maxEntries: number;
  readonly maxTotalUncompressedBytes: number;
  readonly maxEntryUncompressedBytes: number;
  readonly maxCompressionRatio: number;
  readonly allowedCompressionMethods: readonly number[];
}

const MEBIBYTE = 1024 * 1024;

/** Security defaults, not account limits. Callers may lower them for constrained devices. */
export const DEFAULT_ARCHIVE_VALIDATION_POLICY: ArchiveValidationPolicy = {
  maxArchiveBytes: 512 * MEBIBYTE,
  maxEntries: 10_000,
  maxTotalUncompressedBytes: 1024 * MEBIBYTE,
  maxEntryUncompressedBytes: 512 * MEBIBYTE,
  maxCompressionRatio: 200,
  allowedCompressionMethods: [0, 8],
};

export interface ZipEntryInspection {
  readonly name: string;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly compressionMethod: number;
  readonly encrypted: boolean;
  readonly directory: boolean;
  readonly localHeaderOffset: number;
}

export interface ZipInspection {
  readonly ok: boolean;
  readonly entries: readonly ZipEntryInspection[];
  readonly totalCompressedBytes: number;
  readonly totalUncompressedBytes: number;
  readonly diagnostics: readonly FileValidationDiagnostic[];
}

export interface FileValidationOptions {
  readonly expectedKinds?: readonly FileKind[];
  readonly fileName?: string;
  readonly declaredMimeType?: string;
  readonly archivePolicy?: Partial<ArchiveValidationPolicy>;
}

export interface FileValidationResult {
  readonly ok: boolean;
  readonly kind: FileKind;
  readonly diagnostics: readonly FileValidationDiagnostic[];
  readonly archive?: ZipInspection;
}

const ZIP_LOCAL_FILE_HEADER = 0x04034b50;
const ZIP_CENTRAL_DIRECTORY_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const UINT16_MAX = 0xffff;
const UINT32_MAX = 0xffffffff;
const MAX_ZIP_COMMENT_BYTES = UINT16_MAX;

const MIME_TYPES: Readonly<Record<Exclude<FileKind, 'unknown'>, readonly string[]>> = {
  pdf: ['application/pdf'],
  jpeg: ['image/jpeg', 'image/jpg'],
  png: ['image/png'],
  webp: ['image/webp'],
  heic: ['image/heic', 'image/heif'],
  html: ['text/html', 'application/xhtml+xml'],
  zip: ['application/zip', 'application/x-zip-compressed'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  pptx: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

const EXTENSIONS: Readonly<Record<Exclude<FileKind, 'unknown'>, readonly string[]>> = {
  pdf: ['pdf'],
  jpeg: ['jpg', 'jpeg'],
  png: ['png'],
  webp: ['webp'],
  heic: ['heic', 'heif'],
  html: ['html', 'htm'],
  zip: ['zip'],
  docx: ['docx'],
  pptx: ['pptx'],
  xlsx: ['xlsx'],
};

function makeDiagnostic(
  code: FileValidationCode,
  severity: 'error' | 'warning' = 'error',
  details?: Readonly<Record<string, ValidationDetailValue>>,
): FileValidationDiagnostic {
  return details === undefined ? { code, severity } : { code, severity, details };
}

function hasError(diagnostics: readonly FileValidationDiagnostic[]): boolean {
  return diagnostics.some((item) => item.severity === 'error');
}

function resolveArchivePolicy(
  overrides: Partial<ArchiveValidationPolicy> = {},
): ArchiveValidationPolicy {
  const policy: ArchiveValidationPolicy = {
    maxArchiveBytes:
      overrides.maxArchiveBytes ?? DEFAULT_ARCHIVE_VALIDATION_POLICY.maxArchiveBytes,
    maxEntries: overrides.maxEntries ?? DEFAULT_ARCHIVE_VALIDATION_POLICY.maxEntries,
    maxTotalUncompressedBytes:
      overrides.maxTotalUncompressedBytes ??
      DEFAULT_ARCHIVE_VALIDATION_POLICY.maxTotalUncompressedBytes,
    maxEntryUncompressedBytes:
      overrides.maxEntryUncompressedBytes ??
      DEFAULT_ARCHIVE_VALIDATION_POLICY.maxEntryUncompressedBytes,
    maxCompressionRatio:
      overrides.maxCompressionRatio ??
      DEFAULT_ARCHIVE_VALIDATION_POLICY.maxCompressionRatio,
    allowedCompressionMethods:
      overrides.allowedCompressionMethods ??
      DEFAULT_ARCHIVE_VALIDATION_POLICY.allowedCompressionMethods,
  };
  const positiveFiniteValues = [
    policy.maxArchiveBytes,
    policy.maxEntries,
    policy.maxTotalUncompressedBytes,
    policy.maxEntryUncompressedBytes,
    policy.maxCompressionRatio,
  ];
  if (
    positiveFiniteValues.some((value) => !Number.isFinite(value) || value <= 0) ||
    !Number.isSafeInteger(policy.maxEntries) ||
    policy.allowedCompressionMethods.length === 0
  ) {
    throw new RangeError('Archive validation policy values must be positive');
  }
  return policy;
}

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) return false;
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

function containsPdfHeader(bytes: Uint8Array): boolean {
  const searchLength = Math.min(bytes.length, 1024);
  const signature = [0x25, 0x50, 0x44, 0x46, 0x2d];
  for (let offset = 0; offset <= searchLength - signature.length; offset += 1) {
    if (startsWith(bytes, signature, offset)) return true;
  }
  return false;
}

function looksLikeHtml(bytes: Uint8Array): boolean {
  const prefix = bytes.subarray(0, Math.min(bytes.length, 1024));
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(prefix);
  } catch {
    return false;
  }
  const normalized = text.replace(/^\uFEFF/u, '').trimStart().toLocaleLowerCase('en');
  return /^(?:<!doctype\s+html\b|<html\b|<head\b|<body\b)/u.test(normalized);
}

/** Detects content by signature. File extensions and browser-supplied MIME types are ignored. */
export function detectFileKind(bytes: Uint8Array): FileKind {
  if (bytes.length === 0) return 'unknown';
  if (containsPdfHeader(bytes)) return 'pdf';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (
    startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)
  ) {
    return 'webp';
  }
  if (bytes.length >= 12 && startsWith(bytes, [0x66, 0x74, 0x79, 0x70], 4)) {
    const brand = String.fromCharCode(...bytes.subarray(8, 12));
    if (['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1'].includes(brand)) {
      return 'heic';
    }
  }
  if (
    startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]) ||
    startsWith(bytes, [0x50, 0x4b, 0x05, 0x06])
  ) {
    return 'zip';
  }
  if (looksLikeHtml(bytes)) return 'html';
  return 'unknown';
}

function findEndOfCentralDirectory(view: DataView): number | undefined {
  if (view.byteLength < 22) return undefined;
  const firstPossibleOffset = Math.max(0, view.byteLength - 22 - MAX_ZIP_COMMENT_BYTES);
  for (let offset = view.byteLength - 22; offset >= firstPossibleOffset; offset -= 1) {
    if (view.getUint32(offset, true) !== ZIP_END_OF_CENTRAL_DIRECTORY) continue;
    const commentLength = view.getUint16(offset + 20, true);
    if (offset + 22 + commentLength === view.byteLength) return offset;
  }
  return undefined;
}

function decodeEntryName(
  bytes: Uint8Array,
  utf8: boolean,
): { readonly name: string; readonly supported: boolean } {
  if (utf8) {
    try {
      return {
        name: new TextDecoder('utf-8', { fatal: true }).decode(bytes),
        supported: true,
      };
    } catch {
      return { name: '', supported: false };
    }
  }

  if (bytes.some((byte) => byte > 0x7f)) return { name: '', supported: false };
  let name = '';
  for (const byte of bytes) name += String.fromCharCode(byte);
  return { name, supported: true };
}

function isUnsafeArchivePath(name: string): boolean {
  if (name.length === 0 || /[\u0000-\u001f\u007f]/u.test(name)) return true;
  if (name.startsWith('/') || name.startsWith('\\') || /^[a-z]:/iu.test(name)) return true;
  if (name.includes('\\')) return true;
  const segments = name.split('/');
  return segments.some((segment) => segment === '..');
}

function compressionRatio(uncompressedSize: number, compressedSize: number): number {
  if (uncompressedSize === 0) return 1;
  if (compressedSize === 0) return Number.POSITIVE_INFINITY;
  return uncompressedSize / compressedSize;
}

function emptyInspection(diagnostics: readonly FileValidationDiagnostic[]): ZipInspection {
  return {
    ok: !hasError(diagnostics),
    entries: [],
    totalCompressedBytes: 0,
    totalUncompressedBytes: 0,
    diagnostics,
  };
}

/**
 * Inspects ZIP central-directory metadata without inflating any entry.
 * ZIP64 and multi-disk archives are rejected so 32-bit size fields cannot bypass guards.
 */
export function inspectZipArchive(
  bytes: Uint8Array,
  policyOverrides: Partial<ArchiveValidationPolicy> = {},
): ZipInspection {
  const policy = resolveArchivePolicy(policyOverrides);
  const diagnostics: FileValidationDiagnostic[] = [];

  if (bytes.length > policy.maxArchiveBytes) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveTooLarge, 'error', {
        actual: bytes.length,
        maximum: policy.maxArchiveBytes,
      }),
    );
    return emptyInspection(diagnostics);
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocdOffset = findEndOfCentralDirectory(view);
  if (eocdOffset === undefined) {
    diagnostics.push(makeDiagnostic(FILE_VALIDATION_CODES.archiveEocdMissing));
    return emptyInspection(diagnostics);
  }

  const diskNumber = view.getUint16(eocdOffset + 4, true);
  const centralDirectoryDisk = view.getUint16(eocdOffset + 6, true);
  const entriesOnDisk = view.getUint16(eocdOffset + 8, true);
  const totalEntries = view.getUint16(eocdOffset + 10, true);
  const centralDirectorySize = view.getUint32(eocdOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);

  if (diskNumber !== 0 || centralDirectoryDisk !== 0 || entriesOnDisk !== totalEntries) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveMultiDiskUnsupported),
    );
    return emptyInspection(diagnostics);
  }

  if (
    totalEntries === UINT16_MAX ||
    centralDirectorySize === UINT32_MAX ||
    centralDirectoryOffset === UINT32_MAX
  ) {
    diagnostics.push(makeDiagnostic(FILE_VALIDATION_CODES.archiveZip64Unsupported));
    return emptyInspection(diagnostics);
  }

  if (totalEntries > policy.maxEntries) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveTooManyEntries, 'error', {
        actual: totalEntries,
        maximum: policy.maxEntries,
      }),
    );
    return emptyInspection(diagnostics);
  }

  const centralDirectoryEnd = centralDirectoryOffset + centralDirectorySize;
  if (
    centralDirectoryOffset > eocdOffset ||
    centralDirectoryEnd > eocdOffset ||
    centralDirectoryEnd > bytes.length
  ) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveCentralDirectoryInvalid),
    );
    return emptyInspection(diagnostics);
  }

  const entries: ZipEntryInspection[] = [];
  const seenNames = new Set<string>();
  let totalCompressedBytes = 0;
  let totalUncompressedBytes = 0;
  let offset = centralDirectoryOffset;

  for (let entryIndex = 0; entryIndex < totalEntries; entryIndex += 1) {
    if (offset + 46 > centralDirectoryEnd || view.getUint32(offset, true) !== ZIP_CENTRAL_DIRECTORY_HEADER) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveEntryHeaderInvalid, 'error', {
          entryIndex,
        }),
      );
      break;
    }

    const flags = view.getUint16(offset + 8, true);
    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const diskStart = view.getUint16(offset + 34, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const entryEnd = offset + 46 + fileNameLength + extraLength + commentLength;

    if (
      compressedSize === UINT32_MAX ||
      uncompressedSize === UINT32_MAX ||
      localHeaderOffset === UINT32_MAX
    ) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveZip64Unsupported, 'error', {
          entryIndex,
        }),
      );
      break;
    }
    if (diskStart !== 0) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveMultiDiskUnsupported, 'error', {
          entryIndex,
        }),
      );
      break;
    }
    if (entryEnd > centralDirectoryEnd || entryEnd > bytes.length) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveEntryHeaderInvalid, 'error', {
          entryIndex,
        }),
      );
      break;
    }

    const nameBytes = bytes.subarray(offset + 46, offset + 46 + fileNameLength);
    const decoded = decodeEntryName(nameBytes, (flags & 0x0800) !== 0);
    const name = decoded.supported ? decoded.name : `#${entryIndex}`;
    if (!decoded.supported) {
      diagnostics.push(
        makeDiagnostic(
          FILE_VALIDATION_CODES.archiveEntryNameEncodingUnsupported,
          'error',
          { entryIndex },
        ),
      );
    }
    if (decoded.supported && isUnsafeArchivePath(name)) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveUnsafeEntryPath, 'error', {
          entryIndex,
          entry: name,
        }),
      );
    }
    if (seenNames.has(name)) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveDuplicateEntry, 'error', {
          entryIndex,
          entry: name,
        }),
      );
    }
    seenNames.add(name);

    const encrypted = (flags & 0x0001) !== 0;
    if (encrypted) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveEncryptedEntry, 'error', {
          entryIndex,
          entry: name,
        }),
      );
    }
    if (!policy.allowedCompressionMethods.includes(compressionMethod)) {
      diagnostics.push(
        makeDiagnostic(
          FILE_VALIDATION_CODES.archiveCompressionMethodUnsupported,
          'error',
          { entryIndex, entry: name, method: compressionMethod },
        ),
      );
    }
    if (uncompressedSize > policy.maxEntryUncompressedBytes) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveEntryTooLarge, 'error', {
          entryIndex,
          entry: name,
          actual: uncompressedSize,
          maximum: policy.maxEntryUncompressedBytes,
        }),
      );
    }
    const ratio = compressionRatio(uncompressedSize, compressedSize);
    if (ratio > policy.maxCompressionRatio) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveCompressionRatioTooHigh, 'error', {
          entryIndex,
          entry: name,
          ratio,
          maximum: policy.maxCompressionRatio,
        }),
      );
    }

    if (
      localHeaderOffset + 4 > bytes.length ||
      view.getUint32(localHeaderOffset, true) !== ZIP_LOCAL_FILE_HEADER
    ) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.archiveLocalHeaderInvalid, 'error', {
          entryIndex,
          entry: name,
        }),
      );
    }

    totalCompressedBytes += compressedSize;
    totalUncompressedBytes += uncompressedSize;
    entries.push({
      name,
      compressedSize,
      uncompressedSize,
      compressionMethod,
      encrypted,
      directory: name.endsWith('/'),
      localHeaderOffset,
    });
    offset = entryEnd;
  }

  if (offset !== centralDirectoryEnd) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveCentralDirectorySizeMismatch, 'error', {
        expectedEnd: centralDirectoryEnd,
        actualEnd: offset,
      }),
    );
  }

  if (totalUncompressedBytes > policy.maxTotalUncompressedBytes) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveExpandedSizeTooLarge, 'error', {
        actual: totalUncompressedBytes,
        maximum: policy.maxTotalUncompressedBytes,
      }),
    );
  }
  const totalRatio = compressionRatio(totalUncompressedBytes, totalCompressedBytes);
  if (totalRatio > policy.maxCompressionRatio) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.archiveCompressionRatioTooHigh, 'error', {
        ratio: totalRatio,
        maximum: policy.maxCompressionRatio,
      }),
    );
  }

  return {
    ok: !hasError(diagnostics),
    entries,
    totalCompressedBytes,
    totalUncompressedBytes,
    diagnostics,
  };
}

function classifyOoxmlPackage(entries: readonly ZipEntryInspection[]): {
  readonly kind: 'docx' | 'pptx' | 'xlsx' | 'zip';
  readonly diagnostics: readonly FileValidationDiagnostic[];
} {
  const names = new Set(entries.map((entry) => entry.name));
  const mainParts = [
    { kind: 'docx' as const, path: 'word/document.xml' },
    { kind: 'pptx' as const, path: 'ppt/presentation.xml' },
    { kind: 'xlsx' as const, path: 'xl/workbook.xml' },
  ].filter(({ path }) => names.has(path));

  if (mainParts.length === 0) return { kind: 'zip', diagnostics: [] };
  if (mainParts.length > 1) {
    return {
      kind: 'zip',
      diagnostics: [makeDiagnostic(FILE_VALIDATION_CODES.ooxmlAmbiguousPackage)],
    };
  }

  const diagnostics: FileValidationDiagnostic[] = [];
  if (!names.has('[Content_Types].xml')) {
    diagnostics.push(makeDiagnostic(FILE_VALIDATION_CODES.ooxmlMissingContentTypes));
  }
  if (!names.has('_rels/.rels')) {
    diagnostics.push(makeDiagnostic(FILE_VALIDATION_CODES.ooxmlMissingRootRelationships));
  }
  const mainPart = mainParts[0];
  if (mainPart === undefined) {
    return { kind: 'zip', diagnostics };
  }
  return { kind: mainPart.kind, diagnostics };
}

function fileExtension(fileName: string): string {
  const baseName = fileName.replaceAll('\\', '/').split('/').at(-1) ?? '';
  const dotIndex = baseName.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === baseName.length - 1) return '';
  return baseName.slice(dotIndex + 1).toLocaleLowerCase('en');
}

function expectedMainPart(kind: 'docx' | 'pptx' | 'xlsx'): string {
  if (kind === 'docx') return 'word/document.xml';
  if (kind === 'pptx') return 'ppt/presentation.xml';
  return 'xl/workbook.xml';
}

/**
 * Performs signature validation and, for ZIP containers, guarded central-directory and
 * OOXML package inspection. Declared MIME type and extension mismatches are warnings;
 * content mismatches are errors.
 */
export function validateFileBytes(
  bytes: Uint8Array,
  options: FileValidationOptions = {},
): FileValidationResult {
  if (bytes.length === 0) {
    const diagnostics = [makeDiagnostic(FILE_VALIDATION_CODES.emptyFile)];
    return { ok: false, kind: 'unknown', diagnostics };
  }

  const diagnostics: FileValidationDiagnostic[] = [];
  let kind = detectFileKind(bytes);
  let archive: ZipInspection | undefined;

  if (kind === 'zip') {
    archive = inspectZipArchive(bytes, options.archivePolicy);
    diagnostics.push(...archive.diagnostics);
    const classification = classifyOoxmlPackage(archive.entries);
    kind = classification.kind;
    diagnostics.push(...classification.diagnostics);

    const expectedOoxmlKind = options.expectedKinds?.find(
      (candidate): candidate is 'docx' | 'pptx' | 'xlsx' =>
        candidate === 'docx' || candidate === 'pptx' || candidate === 'xlsx',
    );
    if (expectedOoxmlKind !== undefined && kind === 'zip') {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.ooxmlMissingMainPart, 'error', {
          expected: expectedMainPart(expectedOoxmlKind),
        }),
      );
    }
  }

  if (kind === 'unknown') {
    diagnostics.push(makeDiagnostic(FILE_VALIDATION_CODES.unsupportedMagic));
  }

  if (
    options.expectedKinds !== undefined &&
    options.expectedKinds.length > 0 &&
    !options.expectedKinds.includes(kind)
  ) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.expectedKindMismatch, 'error', {
        detected: kind,
        expected: options.expectedKinds.join(','),
      }),
    );
  }

  const declaredMimeType = options.declaredMimeType
    ?.split(';', 1)[0]
    ?.trim()
    .toLocaleLowerCase('en');
  if (
    kind !== 'unknown' &&
    declaredMimeType !== undefined &&
    declaredMimeType !== '' &&
    declaredMimeType !== 'application/octet-stream' &&
    !MIME_TYPES[kind].includes(declaredMimeType)
  ) {
    diagnostics.push(
      makeDiagnostic(FILE_VALIDATION_CODES.declaredMimeMismatch, 'warning', {
        detected: kind,
        declared: declaredMimeType,
      }),
    );
  }

  if (kind !== 'unknown' && options.fileName !== undefined) {
    const extension = fileExtension(options.fileName);
    if (extension !== '' && !EXTENSIONS[kind].includes(extension)) {
      diagnostics.push(
        makeDiagnostic(FILE_VALIDATION_CODES.extensionMismatch, 'warning', {
          detected: kind,
          extension,
        }),
      );
    }
  }

  const result: FileValidationResult = {
    ok: !hasError(diagnostics),
    kind,
    diagnostics,
    ...(archive === undefined ? {} : { archive }),
  };
  return result;
}
